/**
 * Pure date-conversion helpers shared by the Jalali pickers. Kept free of CSS
 * and React imports so they're unit-testable in isolation.
 *
 * Storage invariant: values are ALWAYS Gregorian ISO ('YYYY-MM-DD' for date,
 * 'YYYY-MM-DDTHH:mm' for datetime). Conversion is purely calendrical via
 * date-object (no JS Date / epoch), so there is no timezone drift near
 * midnight regardless of the host TZ.
 */
import { DateObject } from 'react-multi-date-picker'
import persian from 'react-date-object/calendars/persian'
import persian_fa from 'react-date-object/locales/persian_fa'
import gregorian from 'react-date-object/calendars/gregorian'
import gregorian_en from 'react-date-object/locales/gregorian_en'

export type CalendarMode = 'jalali' | 'gregorian'

/** Split a stored Gregorian ISO value into its date + (optional) time halves. */
function splitStored(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T')
  return { date, time }
}

/**
 * Parse a stored Gregorian ISO string into a DateObject in the requested
 * display calendar. Returns null for empty / invalid input. Parsing uses a
 * space separator (not 'T') so date-object never mis-tokenizes it.
 */
export function parseStored(
  value: string,
  isDateTime: boolean,
  displayCalendar: typeof gregorian,
  displayLocale: typeof gregorian_en,
): DateObject | null {
  if (!value) return null
  const { date, time } = splitStored(value)
  if (!date) return null
  const normalized = isDateTime && time ? `${date} ${time}` : date
  const d = new DateObject({
    calendar: gregorian,
    locale: gregorian_en,
    format: isDateTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD',
    date: normalized,
  })
  if (!d.isValid) return null
  return d.convert(displayCalendar, displayLocale)
}

/** Serialize a DateObject (any calendar) back to the stored Gregorian ISO form. */
export function serializeStored(obj: DateObject | null, isDateTime: boolean): string {
  if (!obj) return ''
  const g = obj.convert(gregorian, gregorian_en)
  const datePart = g.format('YYYY-MM-DD')
  return isDateTime ? `${datePart}T${g.format('HH:mm')}` : datePart
}

export const displayCalendarFor = (mode: CalendarMode) =>
  mode === 'gregorian' ? gregorian : persian
export const displayLocaleFor = (mode: CalendarMode) =>
  mode === 'gregorian' ? gregorian_en : persian_fa
